import { useApp } from '@/contexts';

import { useEffect, useState } from 'react';

import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { ConnectionStatusIcon } from '@/components/common';
import type { Credential, IntegrationProvider } from '@/types';

import {
  useGetIntegrationTypesQuery,
  useGetProviderMetadataMutation,
  useTestConnectionMutation,
} from '../api/environmentsApi';
import { useContentfulConnectors } from '../hooks/useContentfulConnectors';
import { getConnectorIcon } from '../utils/connectorIcons';
import { normalizeCredentialType } from '../utils/credentialTypeMapping';
import { getTestEndpointConfig } from '../utils/providerUtils';
import type { MisconfiguredConnector } from '../utils/validateConnectors';

interface ConnectorsListProps {
  tenantId: string;
  envName: string;
  envId: string;
  credentials: Credential[];
  onCredentialClick?: (credential: Credential) => void;
  misconfiguredConnectors: MisconfiguredConnector[];
}

/**
 * Matches dataProtect connectorsHelper.js TestConnection():
 * Checks if all required credential fields are present.
 * Returns true if testing should proceed, false otherwise.
 */
const shouldTestConnection = (
  providerConfig: IntegrationProvider,
  credentialsJson: Record<string, unknown>
): boolean => {
  const notRequired =
    (providerConfig.credentials &&
      providerConfig.credentials.length === 1 &&
      providerConfig.credentials[0] === 'Oauth') ||
    providerConfig.type === 'BrightSpaceValenceApi' ||
    providerConfig.type === 'DocuSign' ||
    providerConfig.type === 'LocalFile';
  if (notRequired) {
    return true;
  }

  let arrayCreds = providerConfig.credentials;
  if (providerConfig.type === 'SFTP') {
    arrayCreds =
      providerConfig.credentials &&
      providerConfig.credentials.filter((o) => {
        if (o === 'privateKey') {
          const i = 'ssh-key';
          const j = 'two-Factor Auth';
          return credentialsJson['credentialType'] === i || credentialsJson['credentialType'] === j;
        }
        if (o === 'passphrase') {
          const j = 'two-Factor Auth';
          return credentialsJson['credentialType'] === j;
        }
        if (o === 'password') {
          const i = 'ssh-key';
          return credentialsJson['credentialType'] !== i;
        }
        return true;
      });
  }
  if (providerConfig.type === 'LingkAdapter' && credentialsJson.rootUrl !== '') {
    return true;
  }
  if (arrayCreds) {
    const hasAllKeys = arrayCreds.every((item) =>
      Object.prototype.hasOwnProperty.call(credentialsJson, item)
    );
    if (hasAllKeys) {
      return !Object.values(credentialsJson).includes('');
    }
  }

  return false;
};

/**
 * Connectors list matching production dataProtect CredBar.js EXACTLY:
 *
 * 1. Fetches providers from integrationtypes/available API
 * 2. For each credential, finds matching providerConfig
 * 3. Checks noCheckConnectionYet → if true, skip (no status icon shown)
 * 4. Checks helpers.TestConnection() → if false, show error icon
 * 5. Calls check() which looks at providerConfig.metadataEndpoint & skipCheckConnection:
 *    - if (!skipCheckConnection && metadataEndpoint) → tryCheckConnection
 *    - else → checkProviderMetadata (which internally also calls tryCheckConnection first)
 * 6. tryCheckConnection routes based on providerConfig.metadataApi
 */
export const ConnectorsList = ({
  tenantId,
  envName,
  envId,
  credentials,
  onCredentialClick,
  misconfiguredConnectors,
}: ConnectorsListProps) => {
  const { appId } = useApp();
  const [connectionStatuses, setConnectionStatuses] = useState<
    Record<string, { checking: boolean; result: string | null }>
  >({});

  const [testConnection] = useTestConnectionMutation();
  const [getProviderMetadata] = useGetProviderMetadataMutation();

  // Fetch providers from API - exactly like dataProtect's callGetProductizedIntegrationTypesForUser
  const { data: providers } = useGetIntegrationTypesQuery();

  // Fetch Contentful connector data for logos
  const { connectors: contentfulConnectors } = useContentfulConnectors();

  // Helper to get Contentful logo URL for a credential
  const getConnectorLogoUrl = (credentialType: string): string | undefined => {
    const entry = contentfulConnectors.find((c) => c.fields.providerType === credentialType);
    return entry?.fields.logo?.fields?.file?.url;
  };

  // Find providerConfig matching Connector.js line 851-856:
  //   const providerType = this.normalizeCredentialTypeNames(c.providerLabel);
  //   const providerConfig = main.providers.find(p => p.type === providerType);
  // Old UI uses providerLabel to find the provider, NOT credentialType.
  // For OAuth types (SalesforceAuthCode→Salesforce), providerLabel already holds the base type.
  // Falls back to credentialType with normalization if providerLabel doesn't match.
  const findProviderConfig = (credential: Credential): IntegrationProvider | undefined => {
    if (!providers) return undefined;

    // Primary: match by providerLabel (same as old UI)
    if (credential.providerLabel) {
      const match = providers.find((p) => p.type === credential.providerLabel);
      if (match) return match;
    }

    // Fallback: match by credentialType with normalization (for edge cases)
    const normalizedType = normalizeCredentialType(credential.credentialType);
    if (!normalizedType) return undefined;
    return providers.find((p) => p.type === normalizedType);
  };

  // Auto-test connections on mount (matches CredBar.js componentDidMount lines 23-47)
  useEffect(() => {
    if (!providers || providers.length === 0) return;

    credentials.forEach((cred) => {
      const providerConfig = findProviderConfig(cred);
      if (!providerConfig) return;

      // Skip if wasJustAdded (matches CredBar line 29)
      if ((cred as { wasJustAdded?: boolean }).wasJustAdded) return;

      // Matches CredBar.js componentDidMount lines 30-46 exactly:
      // 1. TestConnection determines if we should test
      // 2. If doTesting && !noCheckConnectionYet → check()
      // 3. If !doTesting → "404 Error"
      const doTesting = shouldTestConnection(providerConfig, cred.credentialsJson || {});

      if (doTesting) {
        if (!providerConfig.noCheckConnectionYet) {
          checkCredentialConnection(cred, providerConfig);
        }
      } else {
        const credKey = `${cred.providerLabel}-${cred.key}`;
        setConnectionStatuses((prev) => ({
          ...prev,
          [credKey]: { checking: false, result: '404 Error' },
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials.length, providers]);

  /**
   * Check connection for a credential using the exact same two-path logic as CredBar.js check():
   *
   * Path A (line 77): if (!skipCheckConnection && metadataEndpoint)
   *   → tryCheckConnection directly
   *   → success: "200 OK", failure: "404 Error"
   *
   * Path B (line 93): else (skipCheckConnection=true OR metadataEndpoint is empty)
   *   → checkProviderMetadata = tryCheckConnection (may skip) + callGetProviderMetadata
   *   → success: "200 OK" (except LocalFile with empty array → no icon)
   *   → failure: "404 Error"
   */
  const checkCredentialConnection = async (
    credential: Credential,
    providerConfig: IntegrationProvider
  ) => {
    const credKey = `${credential.providerLabel}-${credential.key}`;
    const { metadataEndpoint, skipCheckConnection } = providerConfig;

    // Set checking state (matches CredBar line 64-71)
    setConnectionStatuses((prev) => ({
      ...prev,
      [credKey]: { checking: true, result: null },
    }));

    try {
      if (!skipCheckConnection && metadataEndpoint) {
        // PATH A: Direct tryCheckConnection (CredBar.js line 77-92)
        const endpointConfig = getTestEndpointConfig(providerConfig, envId);
        if (endpointConfig) {
          await testConnection({
            tenantId,
            appId,
            type: providerConfig.type,
            endpoint: endpointConfig.endpoint,
            envId: endpointConfig.envId,
            envName,
            connectorKey: credential.key,
            data:
              endpointConfig.endpoint === 'sfAgent'
                ? undefined
                : {
                    credentialType: providerConfig.credentialType,
                    credentialsJson: credential.credentialsJson,
                    providerLabel: providerConfig.type,
                  },
          }).unwrap();
        }
        setConnectionStatuses((prev) => ({
          ...prev,
          [credKey]: { checking: false, result: '200 OK' },
        }));
      } else {
        // PATH B: checkProviderMetadata (CredBar.js line 93-131)
        if (!skipCheckConnection) {
          const endpointConfig = getTestEndpointConfig(providerConfig, envId);
          if (endpointConfig) {
            await testConnection({
              tenantId,
              appId,
              type: providerConfig.type,
              endpoint: endpointConfig.endpoint,
              envId: endpointConfig.envId,
              envName,
              connectorKey: credential.key,
              data:
                endpointConfig.endpoint === 'sfAgent'
                  ? undefined
                  : {
                      credentialType: providerConfig.credentialType,
                      credentialsJson: credential.credentialsJson,
                      providerLabel: providerConfig.type,
                    },
            }).unwrap();
          }
        }

        // Step 2: callGetProviderMetadata (environmentActions.js line 767-841)
        // If metadataEndpoint is empty, resolves null (line 782-783)
        let metadata: unknown = null;
        if (metadataEndpoint) {
          metadata = await getProviderMetadata({
            tenantId,
            appId,
            metadataEndpoint: providerConfig.metadataEndpoint,
            metadataApi: providerConfig.metadataApi,
            credentialType: providerConfig.credentialType,
            credentialsJson: credential.credentialsJson || {},
            providerType: providerConfig.type,
            envName, // Include environment name for adapter metadata routing
            connectionKey: providerConfig.credentials?.includes('Oauth')
              ? credential.key
              : undefined,
          }).unwrap();
        }

        // Success handling (CredBar line 103-112)
        let checkResult: string | null = '200 OK';
        // LocalFile special case: empty metadata array → no icon (CredBar line 106-109)
        if (
          providerConfig.type === 'LocalFile' &&
          Array.isArray(metadata) &&
          metadata.length === 0
        ) {
          checkResult = null;
        }

        setConnectionStatuses((prev) => ({
          ...prev,
          [credKey]: { checking: false, result: checkResult },
        }));
      }
    } catch (_err) {
      // Error → "404 Error" (CredBar line 117-119 for Path B, or unhandled rejection for Path A)
      setConnectionStatuses((prev) => ({
        ...prev,
        [credKey]: { checking: false, result: '404 Error' },
      }));
    }
  };

  const handleRowClick = (credential: Credential) => {
    if (onCredentialClick) {
      onCredentialClick(credential);
    }
  };

  // Filter out system credentials and credentials with no matching provider
  // (matches Connector.js lines 851-860: normalizeCredentialTypeNames + if(!providerConfig) return null)
  const visibleCredentials = credentials.filter(
    (c) =>
      c.credentialType !== 'AdapterSecret' &&
      c.credentialType !== 'EnvironmentVariables' &&
      findProviderConfig(c) !== undefined
  );

  // Sort alphabetically by Connection Name (display: connectionName || providerLabel)
  const sortedCredentials = [...visibleCredentials].sort((a, b) => {
    const nameA = (
      a.connectionName ||
      findProviderConfig(a)?.label ||
      a.providerLabel ||
      ''
    ).toLowerCase();
    const nameB = (
      b.connectionName ||
      findProviderConfig(b)?.label ||
      b.providerLabel ||
      ''
    ).toLowerCase();
    return nameA.localeCompare(nameB);
  });

  if (visibleCredentials.length === 0) {
    return (
      <Box sx={{ my: 2 }}>
        <Typography sx={{ fontSize: '13px' }}>
          No Data Connectors found for this Environment
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#EEE', width: '100%' }}>
      <TableContainer>
        <Table size="small" sx={{ border: '1px solid #ddd' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '13px', width: 50 }} />
              <TableCell sx={{ fontWeight: 'bold', fontSize: '13px' }}>Connection Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '13px' }}>Connection Key</TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '13px' }}>
                Source/Destination
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '13px' }}>
                Default/API Accessible
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '13px' }} align="center">
                Connection
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCredentials.map((cred, _index) => {
              const credKey = `${cred.providerLabel}-${cred.key}`;
              const status = connectionStatuses[credKey];
              const providerConfig = findProviderConfig(cred);

              // Check if this credential type is misconfigured (matches dataProtect CredBar.js line 147)
              const isMisconfigured = misconfiguredConnectors.some(
                (mc) => mc.credentialType === cred.credentialType
              );

              return (
                <TableRow
                  key={`${cred.providerLabel}-${cred.key}`}
                  onClick={() => handleRowClick(cred)}
                  className="singleConnection"
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: isMisconfigured ? '#F7D1CD' : 'inherit',
                    '&:hover': {
                      backgroundColor: isMisconfigured ? '#F5C1BB' : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <TableCell sx={{ width: '4%', textAlign: 'center' }}>
                    {(() => {
                      const logoUrl = getConnectorLogoUrl(cred.credentialType);
                      return logoUrl ? (
                        <Box
                          component="img"
                          src={logoUrl}
                          alt=""
                          sx={{ width: 24, height: 24, objectFit: 'contain' }}
                        />
                      ) : (
                        getConnectorIcon(cred.providerLabel, cred.credentialType)
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {/* Fallback to providerLabel if connectionName is empty (matches CredBar line 166-170) */}
                    {cred.connectionName ||
                      (providerConfig ? providerConfig.label : cred.providerLabel)}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                    >
                      {cred.key}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: '15%' }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {/* Source/Dest from providerConfig (matches CredBar lines 176-205) */}
                      {/* Show Source ONLY if readerName is exactly "Reader" */}
                      {providerConfig?.readerName === 'Reader' && (
                        <Chip
                          label="Source"
                          size="small"
                          sx={{
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32',
                            fontSize: '11px',
                            height: 22,
                          }}
                        />
                      )}
                      {/* Show Destination ONLY if writerName is exactly "Writer" */}
                      {providerConfig?.writerName === 'Writer' && (
                        <Chip
                          label="Destination"
                          size="small"
                          sx={{
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            fontSize: '11px',
                            height: 22,
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      {cred.defaultConnection && (
                        <Chip label="Default" size="small" color="primary" sx={{ height: 22 }} />
                      )}
                      {cred.ApiAccessible && (
                        <Chip label="API" size="small" color="info" sx={{ height: 22 }} />
                      )}
                      {isMisconfigured && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: '#d32f2f',
                            fontSize: '12px',
                            fontWeight: 500,
                          }}
                        >
                          <WarningIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          Action Required
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  {/* Connection status column: only show if providerConfig exists and noCheckConnectionYet is false */}
                  {/* Matches CredBar line 256: {!providerConfig.noCheckConnectionYet && (...)} */}
                  <TableCell align="center">
                    {providerConfig && !providerConfig.noCheckConnectionYet && status && (
                      <ConnectionStatusIcon
                        checking={status.checking}
                        result={status.result}
                        size={20}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
